export type TrackEventParams = Record<string, string | number | boolean | undefined>;

/** Envoie un évènement à GA4 et/ou Plausible si présents ; silencieux sinon. */
export function trackEvent(eventName: string, params: TrackEventParams = {}): void {
  if (typeof window === 'undefined') return;
  try {
    const cleaned: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) cleaned[key] = value;
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, cleaned);
    }
    if (typeof window.plausible === 'function') {
      window.plausible(eventName, { props: cleaned });
    }
  } catch {
    // tracking ne doit jamais casser l'UI
  }
}

export function trackBlogArticleView(
  articleSlug: string,
  articleTitle: string,
  articleSilo: string,
  readingMinutes: number,
): void {
  trackEvent('blog_article_view', {
    article_slug: articleSlug,
    article_title: articleTitle,
    article_silo: articleSilo,
    reading_minutes: readingMinutes,
  });
}

export function trackBlogScrollDepth(articleSlug: string, depthPercent: 25 | 50 | 75 | 100): void {
  trackEvent('blog_scroll_depth', {
    article_slug: articleSlug,
    depth_percent: depthPercent,
  });
}

export function trackBlogCtaClick(ctaLabel: string, articleSlug?: string): void {
  trackEvent('blog_cta_click', {
    cta_label: ctaLabel,
    ...(articleSlug ? { article_slug: articleSlug } : {}),
  });
}

export function trackBlogIndexView(siloFilter: string): void {
  trackEvent('blog_index_view', {
    silo_filter: siloFilter,
  });
}

export function trackBlogSiloView(siloId: string): void {
  trackEvent('blog_silo_view', {
    silo_id: siloId,
  });
}

export type WebsiteOfferCtaChannel = 'form' | 'whatsapp' | 'phone';

export function trackWebsiteOfferCta(location: string, channel: WebsiteOfferCtaChannel): void {
  trackEvent('website_offer_cta_click', {
    cta_location: location,
    channel,
  });
}

export function trackLead(service: string): void {
  trackEvent('generate_lead', { service });
}

export type ContactChannel = 'whatsapp' | 'phone';

export type ContactService = 'depannage' | 'reparation-telephone' | 'site-web' | 'autre';

export type ContactCtaLocation =
  | 'hero'
  | 'pricing'
  | 'footer'
  | 'cta'
  | 'card'
  | 'contact'
  | 'estimator'
  | 'service-area'
  | 'legal'
  | 'article'
  | 'coverage'
  | 'form'
  | 'modal';

export interface ContactCtaClickParams {
  channel: ContactChannel;
  service: ContactService;
  cta_location: ContactCtaLocation;
}

export type PhoneClickLocation = 'header' | 'footer' | 'hero' | 'cta' | 'contenu';

export function trackPhoneClick(linkUrl: string, clickLocation: PhoneClickLocation): void {
  trackEvent('phone_click', {
    link_url: linkUrl,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    click_location: clickLocation,
  });
}

export type StickyBarSection = 'sticky_bar';

function trackStickyBarEvent(eventName: 'click_phone' | 'click_whatsapp'): void {
  trackEvent(eventName, {
    section: 'sticky_bar' satisfies StickyBarSection,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

export function trackClickPhone(): void {
  trackStickyBarEvent('click_phone');
}

export function trackClickWhatsapp(): void {
  trackStickyBarEvent('click_whatsapp');
}

export function contactCtaClick({ channel, service, cta_location }: ContactCtaClickParams): void {
  trackEvent('contact_cta_click', {
    channel,
    service,
    cta_location,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}
